import {
  forwardRef,
  useEffect,
  useState,
  type FocusEvent,
  type InputHTMLAttributes,
} from 'react';

import { cn } from '../lib/cn';
import { Input } from './input';

const fmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });

/** kuruş → display string ("1.234,56"); null/undefined → ''. */
export function formatLira(kurus: number | null | undefined): string {
  return kurus == null ? '' : fmt.format(kurus / 100);
}

/** Display string → kuruş (accepts tr-TR "1.234,56" and plain "1234.56"); null when empty/invalid. */
export function parseLira(raw: string): number | null {
  const trimmed = raw.trim().replaceAll(' ', '');
  if (!trimmed) return null;
  let normalized: string;
  if (trimmed.includes(',')) {
    // tr-TR: '.' thousands, ',' decimal.
    normalized = trimmed.replaceAll('.', '').replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(trimmed)) {
    // No comma but dot-grouped thousands ("1.234") — grouping, not a decimal.
    normalized = trimmed.replaceAll('.', '');
  } else {
    normalized = trimmed;
  }
  const lira = Number(normalized);
  if (!Number.isFinite(lira) || lira < 0) return null;
  return Math.round(lira * 100);
}

export interface MoneyInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** Amount in kuruş (integer minor units — the repo-wide money convention). */
  value: number | null;
  onValueChange: (kurus: number | null) => void;
}

/**
 * TL-masked input over kuruş values (redesign-admin.md §0 D2): shows the
 * tr-TR-formatted lira amount, edits as free text, commits on change with the
 * parsed kuruş (or null while invalid/empty). Ends the raw `type=number`
 * `/100` fields.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  ({ value, onValueChange, className, onBlur, onFocus, ...props }, ref) => {
    const [text, setText] = useState(() => formatLira(value));
    const [editing, setEditing] = useState(false);

    // Reflect external value changes while not being edited.
    useEffect(() => {
      if (!editing) setText(formatLira(value));
    }, [value, editing]);

    // onFocus/onBlur are destructured and chained (not spread) so a caller's own
    // handlers can't clobber the editing-state bookkeeping.
    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      setEditing(true);
      onFocus?.(e);
    };
    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setEditing(false);
      setText(formatLira(parseLira(text)));
      onBlur?.(e);
    };

    return (
      <div className={cn('relative', className)}>
        <span aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          ₺
        </span>
        <Input
          ref={ref}
          inputMode="decimal"
          className="pl-7 tabular-nums"
          value={text}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={(e) => {
            setText(e.target.value);
            onValueChange(parseLira(e.target.value));
          }}
          {...props}
        />
      </div>
    );
  },
);
MoneyInput.displayName = 'MoneyInput';
